import {useMemo, useEffect, useState} from "react";
import useAsyncError from "@hooks/useAsyncError";
import {Filter} from "@utils/pagination";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {ServiceSurveysWithIdSchema, ServicesWithIdSchema} from "@feature/types";
import AuthenticationService from "@services/AuthenticationService";
import {useIntl} from "react-intl";
import {debounce} from "lodash";

export const useTintometerSurveys = (
  type: "private" | "worksites",
  filters: Filter[],
  pageSize: number,
  includeClosed?: boolean,
) => {

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance() as any;
  const throwError = useAsyncError();

  // state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<{items: any[], total: number}>();  // TODO use a narrower type
  const [page, setPage] = useState<number>(0);
  const [refreshObservable, setRefreshObservable] = useState(false);

  // utilities
  const userData = AuthenticationService.getUserData();


  // handlers
  const fetch = useMemo(
    () => debounce(async (params: object) => {
      setLoading(true);
      try {
        const {count, result, page: pageOverride} = await countryApiRequest.get("/service_surveys/from_user", {params});
        setData({items: result, total: count});
        setPage(pageOverride);
      }
      catch (err) {
        throwError(err);
      }
      finally {
        setLoading(false);
      }
    }, 300), []
  );
  const create = async (
    service: ServicesWithIdSchema,
    onSuccess: (newSurvey: ServiceSurveysWithIdSchema) => void,
  ) => {
    if (type === "worksites")
      throw new Error("Cannot create worksite tintometer surveys");
    setSubmitting(true);
    try {
      const permissions = ["admin", `user_${userData.id}`];
      const payload = {
        name: "",
        service_id: service.id,
        worksite_id: null,
        id_source_surveys: [],
        complete: false,
        closed: false,
        blocked: false,
        r_perm: permissions,
        w_perm: permissions,
        id_creator: userData.id,
        service_version: service.active_version,
        specified_status: 'unspecified',
      };
      const newSurvey = await countryApiRequest.post("/service_surveys", payload);
      setPage(0);  // Reset to first page to see new survey (triggers a new fetch)
      onSuccess(newSurvey);
    }
    catch (err) {
      throwError(err);
    }
    finally {
      setSubmitting(false);
    }
  }
  const delete_ = async (
    surveyId: number,
    onSuccess: () => void,
  ) => {
    if (type === "worksites")
      throw new Error("Cannot delete worksite tintometer surveys");
    try {
      await countryApiRequest.delete(`/service_surveys/${surveyId}`);
      setPage(0);  // Reset to first page to see updated list (triggers a new fetch)
      onSuccess();
    }
    catch (err) {
      throwError(err);
    }
  }
  const duplicate = async (
    sourceSurvey: ServiceSurveysWithIdSchema & {service_name?: string; worksite_name?: string},
    onSuccess: (newSurvey: ServiceSurveysWithIdSchema) => void,
    onError?: (error: any) => void,
    onSafeFinish?: () => void,
  ) => {

    if (type === "private")
      throw new Error("Cannot duplicate to private tintometer surveys");

    setSubmitting(true);
    try {
      const newSurvey = await countryApiRequest.post(
        `/service_surveys/generate_tintometrist_duplicate/${sourceSurvey.id}`,
        {
          generated_survey_name: intl.formatMessage(
            {id: "autogenerated_tintometrist_survey_name"},
            {
              serviceName: sourceSurvey.service_name?.toUpperCase(),
              worksiteName: sourceSurvey.worksite_name?.toUpperCase(),
            }
          ),
        }
      ) as ServiceSurveysWithIdSchema;
      onSuccess(newSurvey);
    } catch (err) {
      if (!!onError) {
        onError(err);
        return;
      }
      throwError(err);
    } finally {
      onSafeFinish?.();
      setSubmitting(false);
    }
  }

  useEffect(() => {
    // Update data on page load, and when filters/page change
    fetch({
      include_closed: includeClosed,
      for_worksite: type === "worksites",
      offset: page * pageSize,
      limit: pageSize,
      ...(filters?.length > 0 ? {filters: encodeURIComponent(JSON.stringify({filters}))} : {}),
    });
  }, [JSON.stringify(filters), includeClosed, refreshObservable]);

  return {
    data,
    loading, submitting,
    create, delete_, duplicate,
    page, setPage: (p: number) => {
      setPage(p);
      setRefreshObservable((prev) => !prev);
    },
    refetch: () => setRefreshObservable((prev) => !prev),
  };
}